import { clearCachedData, DocCache, pendingWork } from '@colplat/core-client';
import { apiFetch } from '../api.js';
import { appHref, globals } from '../globals.js';
import { clearSession, getStoredSession, returnToLogin } from '../auth-storage.js';
import { accountMenuHtml, wireAccountMenu } from './accountMenu.js';
import { readCached } from '../offlineReads.js';
import { askConfirm, escapeHtml } from '@colplat/ui-web';

/**
 * Shows one tile per app the user actually holds a role grant on.
 */
const APP_META: Record<string, { label: string; icon: string }> = {
  administration: { label: 'Administration', icon: '⚙️' },
  webledger: { label: 'WebLedger', icon: '💰' },
};

interface MeResponse {
  userId: string;
  displayName: string;
  roleGrants: Array<{ appId: string; role: string }>;
}

export async function mountLauncher(root: HTMLElement): Promise<void> {
  const session = getStoredSession();
  if (!session) {
    window.location.assign(appHref('/login'));
    return;
  }

  root.innerHTML = `
    <div class="container py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="h4 mb-0">Your apps</h1>
        ${accountMenuHtml()}
      </div>
      <div id="tiles" class="row g-3"></div>
    </div>`;

  /*
   * Seeded from the apps this build knows about and narrowed once `/auth/me`
   * answers. Only the fallback path uses it — where `indexedDB.databases()`
   * exists the stores are enumerated instead — but sign-out has to work
   * before, and without, that request.
   */
  let appIds: string[] = Object.keys(APP_META);
  wireAccountMenu(root, session.userId, {
    onSignOut: () => { void signOut(session.token, appIds); },
  });

  /*
   * Read through the cache, because this one request used to take the whole
   * launcher down offline.
   *
   * `apiFetch` REJECTS when there is no network, the rejection propagated out
   * of this function, and the page was left with its heading and an empty
   * tile row — reported as "the app opens but neither app is visible". Which
   * is the worst possible offline behaviour, because the two things behind
   * those tiles both work offline; only the list of them did not.
   *
   * The cache is platform-scoped rather than per-app, because `/auth/me` is
   * platform data: it says which apps exist for this person, which is the one
   * question that cannot belong to any of them. It is its own IndexedDB and
   * therefore separate from webledger's — so a device that has ONLY ever
   * opened a deep link into webledger, never this page, still has nothing to
   * show here. That case says so rather than rendering an empty row.
   *
   * A 401 still redirects: that is the server answering, and `readCached`
   * deliberately does not fall back on an answer.
   */
  const docCache = new DocCache({ appId: 'platform', env: globals().env });
  await docCache.open();
  const read = await readCached<MeResponse>(
    docCache, `auth:me:${session.userId}`, '/auth/me', session.token);

  if (!read.ok && read.status === 401) {
    returnToLogin();
    return;
  }

  const tiles = root.querySelector<HTMLElement>('#tiles')!;
  if (!read.ok) {
    tiles.innerHTML = '<p class="text-secondary">Your apps have not been listed on this device yet, '
      + 'and there is no connection.</p>';
    return;
  }

  appIds = orderForLauncher([...new Set([...appIds, ...read.data.roleGrants.map((g) => g.appId)])]);
  tiles.innerHTML = appIds.length > 0
    ? appIds.map(tileHtml).join('')
    : '<p class="text-secondary">No apps yet — ask a super-admin to grant you a role.</p>';
}

/**
 * Administration last, whatever order the grants arrived in.
 *
 * It is the app nobody opens to do their work — it is where you go to add a
 * person or change a role, which happens rarely and never urgently. The
 * tiles otherwise keep the order they came in, so adding an app does not
 * shuffle the ones already there.
 */
export function orderForLauncher(appIds: readonly string[]): string[] {
  const last = (id: string): number => (id === 'administration' ? 1 : 0);
  return [...appIds].sort((a, b) => last(a) - last(b));
}

function tileHtml(appId: string): string {
  const meta = APP_META[appId] ?? { label: appId, icon: '▦' };
  return `<div class="col-6 col-md-3">
    <a class="cf-card d-block p-3 text-decoration-none text-body" href="${appHref(`/${appId}`)}">
      <div style="font-size:1.6rem" aria-hidden="true">${meta.icon}</div>
      <div class="fw-semibold mt-2">${escapeHtml(meta.label)}</div>
    </a></div>`;
}

/**
 * Sign out, and leave nothing readable behind.
 *
 * Clearing the token only stops this browser *asking* for more. Everything
 * it had already been given stayed in IndexedDB — and since the replica that
 * is the whole ledger, not a few recently-viewed pages. On a shared laptop
 * that is somebody's accounts left open in a drawer.
 *
 * The outbox is the exception and is left alone: it holds this person's own
 * unsent work, which is not a cache. They are asked first, because "your
 * unsent changes are still on this device" is a thing to know before walking
 * away from it — and told, rather than offered a choice to discard, because
 * discarding is not something a sign-out button should be able to do.
 */
async function signOut(token: string, appIds: readonly string[]): Promise<void> {
  const env = globals().env;
  if (await pendingWork(appIds, env)) {
    // Not `danger`: nothing here is discarded. The unsent work stays on the
    // device either way — this is a thing to know before walking away from
    // it, not a choice to throw it out.
    const go = await askConfirm({
      title: 'Sign out with unsent changes?',
      message: 'Some changes you made have not reached the server yet. They stay on this '
        + 'device and will sync the next time you sign in here.',
      confirm: 'Sign out', cancel: 'Stay signed in',
    });
    if (!go) return;
  }
  try {
    await apiFetch('/auth/logout', { method: 'POST', token });
  } catch {
    // best-effort — clearing what is on this device below is what matters
  }
  await clearCachedData(env, appIds);
  clearSession();
  window.location.assign(appHref('/login'));
}
